import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { X, UserPlus, CheckCircle, AlertCircle, ShieldCheck } from 'lucide-react';
import { replacementApi, Replacement, CreateReplacementRequest } from '../services/replacementApi';

interface ReplacementModalProps {
  connectionId: number;
  professionalName: string;
  mode: 'professional' | 'employer';
  onClose: () => void;
}

const today = () => new Date().toISOString().split('T')[0];

const emptyForm = (): CreateReplacementRequest => ({
  replacement_name: '',
  replacement_email: '',
  replacement_phone: '',
  is_belagom_member: true,
  quality_check_confirmed: false,
  start_date: today(),
  end_date: '',
  reason: 'vakantie',
  reason_description: ''
});

export const ReplacementModal: React.FC<ReplacementModalProps> = ({
  connectionId,
  professionalName,
  mode,
  onClose
}) => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateReplacementRequest>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);

  const queryKey = ['replacements', connectionId, mode];

  const { data: replacements = [], isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      mode === 'professional'
        ? replacementApi.listForProfessional(connectionId)
        : replacementApi.listForEmployer(connectionId)
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateReplacementRequest) => replacementApi.create(connectionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setShowForm(false);
      setForm(emptyForm());
      setFormError(null);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || t('replacement.error');
      setFormError(msg);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ replacementId, status }: { replacementId: number; status: 'completed' | 'cancelled' }) =>
      replacementApi.updateStatus(connectionId, replacementId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!form.is_belagom_member && !form.quality_check_confirmed) {
      setFormError(t('replacement.qualityError'));
      return;
    }

    const payload: CreateReplacementRequest = {
      ...form,
      replacement_phone: form.replacement_phone || undefined,
      end_date: form.end_date || undefined,
      reason_description: form.reason_description || undefined
    };
    createMutation.mutate(payload);
  };

  const activeReplacements = replacements.filter((r) => r.status === 'active');
  const pastReplacements = replacements.filter((r) => r.status !== 'active');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-600" />
                {t('replacement.title')}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {mode === 'professional'
                  ? t('replacement.subtitleProfessional')
                  : t('replacement.subtitleEmployer', { name: professionalName })}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-800">
            <p className="font-medium mb-1">{t('replacement.responsibilityTitle')}</p>
            <p>{t('replacement.responsibilityText')}</p>
          </div>

          {/* Active replacements */}
          {isLoading ? (
            <p className="text-gray-500 text-sm py-4">{t('replacement.loading')}</p>
          ) : (
            <>
              {activeReplacements.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('replacement.activeReplacers')}</h4>
                  <div className="space-y-3">
                    {activeReplacements.map((r) => (
                      <ReplacementCard
                        key={r.id}
                        replacement={r}
                        mode={mode}
                        onComplete={() => updateMutation.mutate({ replacementId: r.id, status: 'completed' })}
                        onCancel={() => updateMutation.mutate({ replacementId: r.id, status: 'cancelled' })}
                        isUpdating={updateMutation.isPending}
                      />
                    ))}
                  </div>
                </div>
              )}

              {activeReplacements.length === 0 && !showForm && (
                <div className="text-center py-6 text-gray-500 text-sm mb-4">
                  {t('replacement.noActive')}
                </div>
              )}

              {/* Past replacements */}
              {pastReplacements.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-500 mb-3">{t('replacement.pastReplacers')}</h4>
                  <div className="space-y-2">
                    {pastReplacements.map((r) => (
                      <ReplacementCard
                        key={r.id}
                        replacement={r}
                        mode={mode}
                        onComplete={() => {}}
                        onCancel={() => {}}
                        isUpdating={false}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Form (professional only) */}
          {mode === 'professional' && (
            <>
              {!showForm ? (
                <button
                  onClick={() => setShowForm(true)}
                  className="w-full py-2.5 px-4 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg text-sm font-medium hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  {t('replacement.addButton')}
                </button>
              ) : (
                <form onSubmit={handleSubmit} className="border border-gray-200 rounded-lg p-5 space-y-4">
                  <h4 className="font-semibold text-gray-800 text-sm">{t('replacement.formTitle')}</h4>

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('replacement.nameLabel')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={form.replacement_name}
                      onChange={(e) => setForm({ ...form, replacement_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={t('replacement.namePlaceholder')}
                    />
                  </div>

                  {/* Belagom member */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('replacement.belagomMemberLabel')} <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="is_belagom_member"
                          checked={form.is_belagom_member === true}
                          onChange={() => setForm({ ...form, is_belagom_member: true, quality_check_confirmed: false })}
                          className="mt-0.5"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-800">{t('replacement.yes')}</span>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <p className="text-xs text-green-700">{t('replacement.belagomGuarantee')}</p>
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="is_belagom_member"
                          checked={form.is_belagom_member === false}
                          onChange={() => setForm({ ...form, is_belagom_member: false })}
                          className="mt-0.5"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-800">{t('replacement.no')}</span>
                          <p className="text-xs text-gray-500 mt-0.5">{t('replacement.selfResponsible')}</p>
                        </div>
                      </label>
                    </div>

                    {!form.is_belagom_member && (
                      <label className="flex items-start gap-3 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.quality_check_confirmed}
                          onChange={(e) => setForm({ ...form, quality_check_confirmed: e.target.checked })}
                          className="mt-0.5"
                          required
                        />
                        <span className="text-sm text-amber-800">{t('replacement.qualityConfirm')}</span>
                      </label>
                    )}
                  </div>

                  {/* Contact */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('replacement.emailLabel')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={form.replacement_email}
                        onChange={(e) => setForm({ ...form, replacement_email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={t('replacement.emailPlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('replacement.phoneLabel')}
                      </label>
                      <input
                        type="tel"
                        value={form.replacement_phone}
                        onChange={(e) => setForm({ ...form, replacement_phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="+31 6 12345678"
                      />
                    </div>
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('replacement.reasonLabel')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.reason}
                      onChange={(e) => setForm({ ...form, reason: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="vakantie">{t('replacement.reasonVakantie')}</option>
                      <option value="ziekte">{t('replacement.reasonZiekte')}</option>
                      <option value="anders">{t('replacement.reasonAnders')}</option>
                    </select>
                    {form.reason === 'anders' && (
                      <textarea
                        value={form.reason_description}
                        onChange={(e) => setForm({ ...form, reason_description: e.target.value })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={2}
                        placeholder={t('replacement.reasonDescPlaceholder')}
                        maxLength={500}
                      />
                    )}
                  </div>

                  {/* Period */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('replacement.startDateLabel')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        required
                        value={form.start_date}
                        onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('replacement.endDateLabel')}
                      </label>
                      <input
                        type="date"
                        value={form.end_date}
                        min={form.start_date}
                        onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {formError && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{formError}</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => { setShowForm(false); setForm(emptyForm()); setFormError(null); }}
                      className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                    >
                      {t('replacement.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={createMutation.isPending}
                      className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      {createMutation.isPending ? t('replacement.submitting') : t('replacement.addButton')}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface ReplacementCardProps {
  replacement: Replacement;
  mode: 'professional' | 'employer';
  onComplete: () => void;
  onCancel: () => void;
  isUpdating: boolean;
}

const ReplacementCard: React.FC<ReplacementCardProps> = ({
  replacement,
  mode,
  onComplete,
  onCancel,
  isUpdating
}) => {
  const { t } = useTranslation('pages');
  const isActive = replacement.status === 'active';

  const statusLabel = {
    active: t('replacement.statusActive'),
    completed: t('replacement.statusCompleted'),
    cancelled: t('replacement.statusCancelled'),
  }[replacement.status] ?? replacement.status;

  const reasonLabel = {
    vakantie: t('replacement.reasonVakantie'),
    ziekte: t('replacement.reasonZiekte'),
    anders: t('replacement.reasonAnders'),
  }[replacement.reason] ?? replacement.reason;

  return (
    <div className={`border rounded-lg p-4 ${isActive ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-gray-900 text-sm">{replacement.replacement_name}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              replacement.status === 'active' ? 'bg-green-100 text-green-800'
              : replacement.status === 'completed' ? 'bg-gray-100 text-gray-700'
              : 'bg-red-100 text-red-700'
            }`}>
              {statusLabel}
            </span>
            {replacement.is_belagom_member ? (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                <ShieldCheck className="w-3 h-3" />
                {t('replacement.belagomMember')}
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                <CheckCircle className="w-3 h-3" />
                {t('replacement.qualityChecked')}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-600 space-y-0.5">
            <p>{replacement.replacement_email}{replacement.replacement_phone ? ` · ${replacement.replacement_phone}` : ''}</p>
            <p>
              {t('replacement.reasonPrefix')} {reasonLabel}
              {replacement.reason === 'anders' && replacement.reason_description ? ` – ${replacement.reason_description}` : ''}
            </p>
            <p>
              {t('replacement.periodPrefix')} {new Date(replacement.start_date).toLocaleDateString('nl-NL')}
              {replacement.end_date
                ? ` ${t('replacement.till')} ${new Date(replacement.end_date).toLocaleDateString('nl-NL')}`
                : ` ${t('replacement.ongoing')}`}
            </p>
          </div>
        </div>
        {mode === 'professional' && isActive && (
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <button
              onClick={onComplete}
              disabled={isUpdating}
              className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {t('replacement.complete')}
            </button>
            <button
              onClick={onCancel}
              disabled={isUpdating}
              className="px-3 py-1.5 text-xs font-medium border border-red-300 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-50"
            >
              {t('replacement.cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
